import type { SerializedError } from '@reduxjs/toolkit'
import { skipToken, type FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { Flex } from 'antd'
import dayjs from 'dayjs'
import { isNil } from 'lodash-es'
import Head from 'next/head'
import { useRouter } from 'next/router'
import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAsync, useIntersection } from 'react-use'
import styled from 'styled-components'
import { z } from 'zod'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchKolSubscriptionStateQuery } from '@/api/workspace-api/kol-api'
import defaultAvatar from '@/assets/images/default-avatar.png'
import { CollectionSearchEventAtProvider } from '@/components/common/contexts/collection-search-event-at-context'
import DataInsufficient from '@/components/kol/common/data-insufficient'
import DurationDropdown from '@/components/kol/common/duration-dropdown'
import useInfluencerInfo from '@/components/kol/hooks/use-influencer-info'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import usePublicKolInfoContext from '@/components/kol/hooks/use-public-kol-info-context'
import useSectionTabs, {
  TabKey,
  tabSchema,
} from '@/components/kol/hooks/use-section-tabs'
import PlatformSelect from '@/components/kol/navigation/platform-select'
import SectionAnchor from '@/components/kol/navigation/section-anchor'
import SectionTabs from '@/components/kol/navigation/section-tabs'
import TopSummary from '@/components/kol/navigation/top-summary'
import SubscribeCta from '@/components/kol/subscribe-cta'
import { HeaderWrapper as KolSelfHeaderWrapper } from '@/components/layout/header'
import { HeaderWrapper } from '@/components/layout/irm/irm-header'
import { IRMLayout } from '@/components/layout/irm/irm-layout'
import {
  ContentWrapper,
  KolDetailLayout as KolSelfPageLayout,
} from '@/components/layout/kol-detail-layout'
import AppFooter from '@/components/public-page/footer'
import AppHeader, {
  HeaderWrapper as PublicPageHeaderWrapper,
} from '@/components/public-page/header'
import { useAuth } from '@/hooks/use-auth'
import { Page } from '@/hooks/use-authorization/constants'
import useDefaultPage from '@/hooks/use-authorization/use-default-page'
import { useDispatch } from '@/hooks/use-dispatch'
import useEffectUntilResolve from '@/hooks/use-effect-until-resolve'
import useIsClient from '@/hooks/use-is-client'
import useIsDefaultSearch from '@/hooks/use-is-default-search'
import useKolDetailDuration from '@/hooks/use-kol-detail-duration'
import { usePermissions } from '@/hooks/use-permissions'
import { useIntl } from '@/i18n/hooks/use-intl'
import { setKolUUID } from '@/store/kol-detail'
import { KolSource } from '@/types/api/search'
import { Optional } from '@/types/optional'
import type { InfluencerInfo } from '@/types/schema/influencer/influencer-info-schema'
import type { KolSchemaModel } from '@/types/schema/kol-schema'
import type { KolInfo } from '@/types/schema/kol/kol-info-schema'
import type { PublicKolInfo } from '@/types/schema/kol/public-kol-info-schema'
import { ampli, type VisitKolDetailProperties } from '@/utils/ampli'
import env from '@/utils/env'
import { getThumbnail } from '@/utils/get-thumbnails'
import { convertInfluencerInfoToKolSchemaModel } from '@/utils/influencer/convert-influencer-info-to-kol-schema-model'
import isExternalOrigin from '@/utils/is-external-origin'
import { convertKolInfoToKolSchemaModel } from '@/utils/kol/convert-kol-info-to-kol-schema-model'
import { convertPublicKolInfoToKolSchemaModel } from '@/utils/kol/convert-public-kol-info-to-kol-schema-model'
import { getKolPlatformDatumFromLinks } from '@/utils/kol/get-kol-platform-datum-from-links'
import logger from '@/utils/logger'
import { goToPage } from '@/utils/routes/go-to-page'
import { toVoidFunction } from '@/utils/to-void-function'

const visitKolDetailPropertiesFromSchema =
  z.custom<VisitKolDetailProperties['from']>()

interface KolDetailLayoutProps {
  children: ReactNode
}

const KolDetailLayout: FunctionComponent<KolDetailLayoutProps> = ({
  children,
}) => {
  const { isDefaultSearch } = useIsDefaultSearch()
  const isClient = useIsClient()
  const router = useRouter()
  const defaultPage = useDefaultPage()
  const {
    page,
    params,
    searchParams,
    setSearchParams,
    fragmentIdentifier,
    isPublicKolDetailPage,
  } = useKolDetailRoute()
  const platformTabsWrapperRef = useRef<HTMLDivElement>(null)
  const platformTabsIntersection = useIntersection(platformTabsWrapperRef, {
    threshold: 0.75,
  })

  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState<TabKey>('overview')
  const { userInfo } = useAuth()
  const { data: userStatus } = useFetchUserStatusQuery()
  const publicKolInfoContext = usePublicKolInfoContext()
  const { withKolPermission } = usePermissions()

  const {
    kolInfo: kolInfoByAd,
    isLocked,
    isError: isErrorKolInfo,
    error: kolInfoError,
    isFetching: isFetchingKolInfo,
  } = useKolInfo(
    page === Page.KolId ? params.data?.kolId : undefined,
    page === Page.KolId ? searchParams.data?.kolDcIdHashed : undefined,
  )
  const {
    influencerInfo,
    isFetching: isFetchingInfluencerInfo,
    isError: isErrorInfluencerInfo,
    error: influencerInfoError,
  } = useInfluencerInfo(
    page === Page.KolSelf ? userInfo?.kolId : params.data?.kolId,
  )
  const tabOptions = useSectionTabs(searchParams.data?.platform)
  const { duration, enable3MonthDuration } = useKolDetailDuration()
  const {
    data: subscriptionStateResponse,
    isLoading: isSubscriptionStateLoading,
    isError: isSubscriptionStateError,
    error: subscriptionStateError,
  } = useFetchKolSubscriptionStateQuery(
    userStatus?.currentWorkspaceId && kolInfoByAd?.id
      ? {
          workspaceId: userStatus.currentWorkspaceId,
          radar_uuid: kolInfoByAd?.id,
          dc_id_hashed: kolInfoByAd?.dc_id_hashed,
        }
      : skipToken,
  )

  const tabs = tabOptions.map((tabOption) => tabOption.key)
  const isKolSelfPage = [Page.KolSelf, Page.KolAgencyId].includes(page)
  const parentLayout = ((): Optional<
    FunctionComponent<KolDetailLayoutProps>
  > => {
    if (isPublicKolDetailPage) {
      return undefined
    }

    return isKolSelfPage ? KolSelfPageLayout : IRMLayout
  })()

  const kolInfo = ((): Optional<KolInfo | PublicKolInfo | InfluencerInfo> => {
    if (isPublicKolDetailPage) {
      return publicKolInfoContext?.publicKolInfo
    }

    if (withKolPermission) {
      return influencerInfo
    }

    return kolInfoByAd
  })()
  const kolSchemaModel = ((): Optional<KolSchemaModel> => {
    if (isPublicKolDetailPage) {
      return publicKolInfoContext?.publicKolInfo
        ? convertPublicKolInfoToKolSchemaModel(
            publicKolInfoContext.publicKolInfo,
          )
        : undefined
    }

    if (withKolPermission) {
      return influencerInfo
        ? convertInfluencerInfoToKolSchemaModel(influencerInfo)
        : undefined
    }

    return kolInfoByAd ? convertKolInfoToKolSchemaModel(kolInfoByAd) : undefined
  })()
  const platforms = kolInfo
    ? getKolPlatformDatumFromLinks(kolInfo.links, kolInfo.kpi)
    : []
  const platformData = searchParams.success
    ? platforms.find((data) => data.shortcode === searchParams.data.platform)
    : undefined

  const handleActiveTabChange = (key: string): void => {
    const tabKey = tabSchema.safeParse(key)
    if (tabKey.success) {
      setActiveTab(tabKey.data)
    }
  }

  const isFirstTriggerWithinOneDay = useMemo(() => {
    if (!subscriptionStateResponse || isSubscriptionStateLoading) {
      return
    }

    if (!subscriptionStateResponse.isCrawlerRunning) {
      return false
    }

    const oneDayAgo = dayjs().subtract(1, 'day')
    const firstTriggerDate = dayjs.unix(
      subscriptionStateResponse.firstTriggerAt,
    )
    return firstTriggerDate.isAfter(oneDayAgo)
  }, [subscriptionStateResponse, isSubscriptionStateLoading])

  const needToShowSubscribeUi =
    !isNil(isFirstTriggerWithinOneDay) && isFirstTriggerWithinOneDay

  const error = ((): FetchBaseQueryError | SerializedError | undefined => {
    if (isPublicKolDetailPage) {
      return undefined
    }

    if (withKolPermission) {
      return influencerInfoError
    }

    return kolInfoError
  })()

  const isError = ((): boolean => {
    if (isPublicKolDetailPage) {
      return false
    }

    if (withKolPermission) {
      return isErrorInfluencerInfo
    }

    return isErrorKolInfo
  })()

  const isLoading = ((): boolean => {
    if (needToShowSubscribeUi && platforms.length === 0) {
      return false
    }

    if (withKolPermission) {
      return isFetchingInfluencerInfo
    }

    return isFetchingKolInfo || !platformData
  })()

  // 檢查 kolInfo 是否符合預期，不符合預期就導走
  useEffectUntilResolve(
    toVoidFunction(async (resolve): Promise<void> => {
      if (
        !kolInfo ||
        !subscriptionStateResponse ||
        isSubscriptionStateLoading
      ) {
        return
      }

      resolve()

      /**
       *  如果沒有平台資訊的話，就檢查該 kol 是否是近一天內建立的新網紅 ref: https://www.notion.so/ikala/BE-Task-Radar-88efcf77bd10425a9c35e5fc532f6d72?pvs=4#fa20ae70e4794e3bb7255b7d4569316c
       *    - 如果是：允許進入 kd 頁，並顯示爬取中 UI
       *    - 如果不是：假設爬蟲狀態異常，導回 default page
       */
      if (
        platforms.length === 0 &&
        !isNil(isFirstTriggerWithinOneDay) &&
        !isFirstTriggerWithinOneDay
      ) {
        await router.replace(defaultPage)
      }
    }),
    [kolInfo, subscriptionStateResponse, platforms.length],
  )

  // 自動加上 platform effect
  useEffectUntilResolve(
    toVoidFunction(async (resolve): Promise<void> => {
      if (!kolInfo || platforms.length === 0) {
        return
      }

      resolve()

      if (searchParams.data?.platform) {
        return
      }

      await setSearchParams(
        {
          ...searchParams.data,
          platform: platforms[0].shortcode,
        },
        fragmentIdentifier.data,
      )
    }),
    [kolInfo, subscriptionStateResponse, platforms.length],
  )

  useAsync(async (): Promise<void> => {
    if (!isError) {
      return
    }

    if (error) {
      logger.error(new Error('fetch kol info error', { cause: error }))
    }

    await goToPage(defaultPage)
  }, [isError])

  useAsync(async (): Promise<void> => {
    if (!isSubscriptionStateError) {
      return
    }

    if (subscriptionStateError) {
      logger.error(
        new Error('fetch kol subscription state error', {
          cause: subscriptionStateError,
        }),
      )
    }

    await goToPage(defaultPage)
  }, [isSubscriptionStateError])

  useEffect(() => {
    if (!kolInfo?.id) {
      return
    }

    dispatch(setKolUUID(kolInfo.id))
  }, [dispatch, kolInfo?.id])

  useEffectUntilResolve(
    (resolve): void => {
      if (isSubscriptionStateLoading || !kolInfo || page !== Page.KolId) {
        return
      }

      const from = ((): VisitKolDetailProperties['from'] | undefined => {
        const parseResult = visitKolDetailPropertiesFromSchema.safeParse(
          searchParams.data?.from,
        )

        return parseResult.success ? parseResult.data : undefined
      })()

      // 無論是是否建立，只要有 kolInfo 和 platform 資料
      if (searchParams.data?.platform) {
        resolve()

        ampli.visitKolDetail({
          kolId: kolInfo.id,
          kolName: kolInfo.name,
          kolCategory: kolInfo.customized_tags_names,
          keyword: searchParams.data?.keyword,
          platform: searchParams.data.platform,
          kolCountry: kolInfo.country_code ?? undefined,
          from,
          fromKolId: searchParams.data?.fromKolId,
          fromKolDcId: searchParams.data?.fromKolDcIdHashed,
          fromKolName: searchParams.data?.fromKolName,
          aiType: searchParams.data?.aiType,
          aiFilterType: searchParams.data?.aiFilterType,
          isFilterBudget: searchParams.data?.isFilterBudget,
          isDefault: isDefaultSearch,
          level: searchParams.data?.level ?? 1,
          isNewToPlatform: searchParams.data?.isNewToPlatform ?? false,
          dataSource: searchParams.data?.dataSource ?? KolSource.DataCenter,
          isExternalLink: isExternalOrigin(),
          isClickedFromDrawer: searchParams.data?.isClickedFromDrawer,
        })
      } else if (needToShowSubscribeUi && platforms.length === 0) {
        resolve()

        // 情境：剛建立的網紅還沒有 platforms 資料, platform 送空字串
        ampli.visitKolDetail({
          kolId: kolInfo.id,
          kolName: kolInfo.name,
          kolCategory: kolInfo.customized_tags_names,
          keyword: searchParams.data?.keyword,
          platform: '',
          kolCountry: kolInfo.country_code ?? undefined,
          from,
          fromKolId: searchParams.data?.fromKolId,
          fromKolDcId: searchParams.data?.fromKolDcIdHashed,
          fromKolName: searchParams.data?.fromKolName,
          aiType: searchParams.data?.aiType,
          aiFilterType: searchParams.data?.aiFilterType,
          isFilterBudget: searchParams.data?.isFilterBudget,
          isDefault: isDefaultSearch,
          level: searchParams.data?.level ?? 1,
          isNewToPlatform: searchParams.data?.isNewToPlatform ?? false,
          dataSource: searchParams.data?.dataSource ?? KolSource.DataCenter,
          isExternalLink: isExternalOrigin(),
          isClickedFromDrawer: searchParams.data?.isClickedFromDrawer,
        })
      }
    },
    [
      needToShowSubscribeUi,
      isSubscriptionStateLoading,
      kolInfo,
      page,
      platforms.length,
      searchParams.data?.platform,
    ],
  )

  if (!isClient && !isPublicKolDetailPage) {
    return (
      <KolDetailHead
        isServerSide
        isKolSelfPage={withKolPermission}
        metaInfo={
          publicKolInfoContext?.publicKolInfoForOpenGraph
            ? {
                id: publicKolInfoContext.publicKolInfoForOpenGraph.id,
                name: publicKolInfoContext.publicKolInfoForOpenGraph.name,
                maxAvgPvRate3m:
                  publicKolInfoContext.publicKolInfoForOpenGraph
                    .max_avg_pvRate_3m,
                maxInteractiveRate3m:
                  publicKolInfoContext.publicKolInfoForOpenGraph
                    .max_interactive_rate_3m,
                follower:
                  publicKolInfoContext.publicKolInfoForOpenGraph.follower,
              }
            : undefined
        }
      />
    )
  }

  return (
    <CollectionSearchEventAtProvider at='KOL Detail'>
      <KolDetailHead
        isKolSelfPage={withKolPermission}
        isServerSide={false}
        metaInfo={
          kolInfo
            ? {
                id: kolInfo.id,
                name: kolInfo.name,
                maxAvgPvRate3m: kolInfo.max_avg_pvRate_3m,
                maxInteractiveRate3m: kolInfo.max_interactive_rate_3m,
                follower: kolInfo.follower,
              }
            : undefined
        }
      />
      <Container>
        <LayoutWrapper as={parentLayout}>
          {isPublicKolDetailPage && (
            <div>
              <AppHeader />
            </div>
          )}
          <div ref={platformTabsWrapperRef}>
            <PlatformSelect
              isLoading={isLoading}
              isUpdating={needToShowSubscribeUi}
              kolId={kolInfo?.id}
              layout='tabs'
              platformData={platformData}
              platforms={platforms}
            />
          </div>
          <SectionAnchor tabs={tabs} onChange={handleActiveTabChange} />
          <StickyHeader>
            <TopSummary
              isLoading={isLoading}
              keyword={searchParams.data?.keyword}
              kolInfo={kolSchemaModel}
              platformData={platformData}
            />
            <SectionTabs
              activeTab={activeTab}
              kolInfo={kolSchemaModel}
              tabBarExtraContent={
                (platformTabsIntersection?.isIntersecting ?? !isClient)
                  ? undefined
                  : {
                      left: (
                        <PlatformSelect
                          isLoading={isLoading}
                          kolId={kolInfo?.id}
                          layout='dropdown'
                          platformData={platformData}
                          platforms={platforms}
                        />
                      ),
                      right: (
                        <Flex gap={4}>
                          {kolInfoByAd?.is_expired && (
                            <DataInsufficient style='compact' />
                          )}
                          <DurationDropdown
                            disabledSixMonths={
                              isPublicKolDetailPage ? true : isLocked
                            }
                            enable3MonthDuration={enable3MonthDuration}
                            kolInfo={kolSchemaModel}
                            value={duration}
                          />
                        </Flex>
                      ),
                    }
              }
              tabOptions={tabOptions}
              onChange={handleActiveTabChange}
            />
          </StickyHeader>
          <PageContainer>
            {needToShowSubscribeUi ? (
              <SubscribeCta
                isSubscribe={subscriptionStateResponse?.isSubscribe}
                kolInfo={kolSchemaModel}
                platform={searchParams.data?.platform}
              />
            ) : (
              children
            )}
          </PageContainer>
          {isPublicKolDetailPage && <AppFooter />}
        </LayoutWrapper>
      </Container>
    </CollectionSearchEventAtProvider>
  )
}

interface KolDetailHeaderProps {
  isKolSelfPage: boolean
  isServerSide: boolean
  metaInfo?: {
    id: string
    name: string
    maxAvgPvRate3m: number
    maxInteractiveRate3m: number
    follower: number
  }
}
const KolDetailHead: FunctionComponent<KolDetailHeaderProps> = ({
  isKolSelfPage,
  isServerSide,
  metaInfo,
}) => {
  const { formatMessage, formatNumber } = useIntl()

  return (
    <Head>
      <title>
        {formatMessage(
          {
            id: 'kol:meta_open_graph_title',
          },
          {
            kolName: metaInfo?.name ?? (isServerSide ? '' : 'Loading...'),
          },
        )}
      </title>
      {!isKolSelfPage && (
        <link
          href={`${env.LANDING_URL}/influencer/${metaInfo?.id}`}
          rel='canonical'
        />
      )}
      <meta
        content={metaInfo?.id ? getThumbnail(metaInfo.id) : defaultAvatar}
        property='og:image:url'
      />
      <meta content='150' property='og:image:width' />
      <meta content='150' property='og:image:height' />
      <meta
        content={formatMessage(
          { id: 'kol:meta_open_graph_description' },
          {
            kolName: metaInfo?.name,
            followerCount: metaInfo?.follower
              ? formatNumber(metaInfo.follower, { isCompactDisplay: true })
              : 0,
            viewRate: metaInfo?.maxAvgPvRate3m
              ? formatNumber(metaInfo.maxAvgPvRate3m, {
                  isPercentageDisplay: true,
                })
              : 0,
            engagementRate: metaInfo?.maxInteractiveRate3m
              ? formatNumber(metaInfo.maxInteractiveRate3m, {
                  isPercentageDisplay: true,
                })
              : 0,
          },
        )}
        name='description'
      />
      <meta
        content={formatMessage(
          { id: 'kol:meta_open_graph_keyword' },
          {
            kolName: metaInfo?.name,
          },
        )}
        name='keywords'
      />
    </Head>
  )
}

const Container = styled.div`
  background: ${({ theme }): string => theme.colors.bg};

  ${HeaderWrapper} {
    position: sticky;
  }

  ${KolSelfHeaderWrapper} {
    position: sticky;
  }

  ${PublicPageHeaderWrapper} {
    position: sticky;
  }

  ${ContentWrapper} {
    background: ${({ theme }): string => theme.colors.bg};
  }
`

const LayoutWrapper = styled.div``

const StickyHeader = styled.div`
  top: 0;
  position: sticky;
  background: ${({ theme }): string => theme.colors.background.white};
  box-shadow: 0 3px 6px 0 rgb(0, 0, 0, 0.06);
  z-index: 10;
`

const PageContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 10px 25px 0;

  @media (min-width: 981px) {
    padding-left: 45px;
    padding-right: 45px;
  }
`

export default KolDetailLayout
